import { useEffect, useState, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { trpc } from '../../lib/trpc.js';
import { routes } from '../../routes.js';

interface Result {
  id: string;
  label: string;
  sub?: string;
  to: string;
  group: 'Clients' | 'Engagements' | 'Projects' | 'Deliverables';
}

/**
 * Cmd/Ctrl+K command palette for the agency side. Searches across
 * clients, engagements, projects, and deliverables; arrow keys + Enter
 * to navigate.
 */
export function CommandPalette({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [query, setQuery] = useState('');
  const [activeIdx, setActiveIdx] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  const trimmed = query.trim();
  const enabled = open && trimmed.length >= 2;
  const searchQ = trpc.search.global.useQuery({ query: trimmed }, { enabled, staleTime: 30_000 });

  const results = useMemo<Result[]>(() => {
    if (!searchQ.data) return [];
    const out: Result[] = [];
    searchQ.data.clients.forEach((c) =>
      out.push({ id: c.id, label: c.name, group: 'Clients', to: routes.clientDetail(c.id) }),
    );
    searchQ.data.engagements.forEach((e) =>
      out.push({
        id: e.id,
        label: e.name,
        sub: e.subtitle,
        group: 'Engagements',
        to: routes.engagementDetail(e.id),
      }),
    );
    searchQ.data.projects.forEach((p) =>
      out.push({
        id: p.id,
        label: p.name,
        sub: p.subtitle,
        group: 'Projects',
        to: routes.project(p.id),
      }),
    );
    searchQ.data.deliverables.forEach((d) =>
      out.push({
        id: d.id,
        label: d.name,
        sub: d.subtitle,
        group: 'Deliverables',
        to: routes.deliverable(d.id),
      }),
    );
    return out;
  }, [searchQ.data]);

  // Reset when opening + autofocus.
  useEffect(() => {
    if (open) {
      setQuery('');
      setActiveIdx(0);
      // Defer to let the modal mount.
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [open]);

  useEffect(() => {
    setActiveIdx(0);
  }, [trimmed]);

  // Keyboard navigation.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActiveIdx((i) => Math.min(i + 1, Math.max(0, results.length - 1)));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActiveIdx((i) => Math.max(i - 1, 0));
      } else if (e.key === 'Enter') {
        const hit = results[activeIdx];
        if (hit) {
          e.preventDefault();
          navigate(hit.to);
          onClose();
        }
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, results, activeIdx, navigate, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-scope-navy/40 p-4 pt-[10vh]"
      onClick={onClose}
    >
      <div
        className="w-full max-w-xl overflow-hidden rounded-xl bg-white shadow-scope-lg"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Search"
      >
        <div className="flex items-center gap-3 border-b border-scope-border-soft px-4 py-3">
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="text-scope-text-muted"
          >
            <circle cx="11" cy="11" r="8" />
            <path d="m21 21-4.35-4.35" />
          </svg>
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search clients, engagements, projects, deliverables…"
            className="flex-1 bg-transparent text-sm text-scope-navy placeholder:text-scope-text-faint focus:outline-none"
          />
          <kbd className="hidden rounded border border-scope-border bg-scope-cream-warm px-1.5 py-0.5 text-2xs font-mono text-scope-text-muted sm:inline">
            Esc
          </kbd>
        </div>

        <div className="max-h-[60vh] overflow-y-auto">
          {trimmed.length < 2 ? (
            <div className="px-4 py-8 text-center text-xs text-scope-text-muted">
              Start typing to search.
            </div>
          ) : searchQ.isLoading ? (
            <div className="px-4 py-8 text-center text-xs text-scope-text-muted">Searching…</div>
          ) : results.length === 0 ? (
            <div className="px-4 py-8 text-center text-xs text-scope-text-muted">
              No matches for "{trimmed}".
            </div>
          ) : (
            <PaletteResults
              results={results}
              activeIdx={activeIdx}
              onPick={(to) => {
                navigate(to);
                onClose();
              }}
            />
          )}
        </div>

        <div className="flex items-center justify-between border-t border-scope-border-soft px-4 py-2 text-2xs text-scope-text-muted">
          <span>
            <kbd className="rounded border border-scope-border px-1">↑↓</kbd> navigate{' '}
            <kbd className="ml-2 rounded border border-scope-border px-1">↵</kbd> open
          </span>
          <span>
            <kbd className="rounded border border-scope-border px-1">⌘K</kbd> /{' '}
            <kbd className="rounded border border-scope-border px-1">Ctrl K</kbd>
          </span>
        </div>
      </div>
    </div>
  );
}

function PaletteResults({
  results,
  activeIdx,
  onPick,
}: {
  results: Result[];
  activeIdx: number;
  onPick: (to: string) => void;
}) {
  // Group while preserving original order.
  const groups = new Map<string, Result[]>();
  results.forEach((r) => {
    const list = groups.get(r.group) ?? [];
    list.push(r);
    groups.set(r.group, list);
  });

  let runningIdx = -1;

  return (
    <div className="py-1">
      {[...groups.entries()].map(([group, items]) => (
        <div key={group}>
          <div className="px-4 pb-1 pt-3 text-2xs font-semibold uppercase tracking-wider text-scope-text-muted">
            {group}
          </div>
          {items.map((r) => {
            runningIdx++;
            const isActive = runningIdx === activeIdx;
            return (
              <button
                key={r.id}
                onClick={() => onPick(r.to)}
                onMouseEnter={() => {
                  /* could lift activeIdx but keeps things simple */
                }}
                className={[
                  'flex w-full items-center justify-between gap-3 px-4 py-2 text-left text-sm transition',
                  isActive
                    ? 'bg-scope-cream-warm text-scope-navy'
                    : 'text-scope-navy hover:bg-scope-cream-warm/60',
                ].join(' ')}
              >
                <span className="truncate font-medium">{r.label}</span>
                {r.sub && (
                  <span className="ml-auto truncate text-xs text-scope-text-muted">{r.sub}</span>
                )}
              </button>
            );
          })}
        </div>
      ))}
    </div>
  );
}
